// 대시보드 — Bento Grid + 3D 틸트 + 카운터 + 가로 스크롤 카드
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Counter } from '../components/Counter'
import { Skeleton, SkeletonRows } from '../components/Skeleton'
import { SocialProof } from '../components/SocialProof'
import { useGlobalReveal } from '../hooks/useReveal'
import { api } from '../lib/api'
import { useT } from '../lib/i18n'

interface Summary {
  productionOrders: number
  runningWorkOrders: number
  avgOEE: number
  defectRate: number
  openAndon: number
  onDutyWorkers: number
  dueMaintenance7d: number
  inventoryAccuracy: number | null
  inventoryCountSamples: number
}

interface EqStatus {
  equipment_id: string
  equipment_name: string
  operation_rate: number
  performance_rate: number
  quality_rate: number
  oee: number
  reported_at: string
}

export function DashboardPage() {
  useGlobalReveal()
  const { t } = useT()
  const [s, setS] = useState<Summary | null>(null)
  const [eq, setEq] = useState<EqStatus[] | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const [summary, status] = await Promise.all([
          api.get<Summary>('/dashboard/summary'),
          api.get<EqStatus[]>('/equipment/status'),
        ])
        if (!active) return
        setS(summary)
        setEq(status)
      } catch (e: any) {
        if (active) setErr(e?.message ?? t('toast.fetchFailed'))
      }
    })()
    return () => { active = false }
  }, [t])

  return (
    <main id="main" className="container" style={{ paddingBlock: '2rem' }}>
      <section className="stack reveal" aria-labelledby="hero-h">
        <span className="badge info" style={{ width: 'fit-content' }}>
          <FontAwesomeIcon icon="bolt" /> {t('dashboard.realtimeBadge')}
        </span>
        <h1 id="hero-h">
          {t('dashboard.hero1')}{' '}
          <span style={{ background: 'linear-gradient(135deg,var(--color-accent),var(--color-accent-2))', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
            {t('dashboard.hero2')}
          </span>
        </h1>
        <p className="muted" style={{ maxWidth: 640 }}>
          {t('dashboard.intro')}
        </p>
      </section>

      <section
        aria-label="KPI"
        className="bento reveal"
        style={{ marginBlockStart: '2rem' }}
      >
        <Card className="b-1 glass" tilt spotlight>
          <span className="badge info">
            <FontAwesomeIcon icon="gauge" /> {t('dashboard.avgOEE')}
          </span>
          <p className="mega-num" style={{ margin: '0.4rem 0 0' }}>
            {s ? <Counter value={s.avgOEE} decimals={1} suffix="%" /> : <Skeleton h={64} />}
          </p>
          <p className="muted" style={{ marginBottom: 0, marginTop: '0.6rem' }}>
            {t('dashboard.avgOEE.note')}
          </p>
        </Card>

        <Card className="b-2 glass" tilt>
          <span className="badge success">
            <FontAwesomeIcon icon="play" /> {t('dashboard.runningWO')}
          </span>
          <p className="kpi-num">
            {s ? <Counter value={s.runningWorkOrders} suffix={t('common.units.count')} /> : <Skeleton h={36} />}
          </p>
        </Card>

        <Card className="b-3 glass" tilt>
          <span className="badge warn">
            <FontAwesomeIcon icon="triangle-exclamation" /> {t('dashboard.defectRate30d')}
          </span>
          <p className="kpi-num">
            {s ? <Counter value={s.defectRate} decimals={2} suffix="%" /> : <Skeleton h={36} />}
          </p>
        </Card>

        <Card className="b-4 glass" tilt>
          <span className="badge">
            <FontAwesomeIcon icon="clipboard-list" /> {t('dashboard.productionOrders')}
          </span>
          <p className="kpi-num">
            {s ? <Counter value={s.productionOrders} suffix={t('common.units.count')} /> : <Skeleton h={36} />}
          </p>
          <p className="muted" style={{ margin: '0.4rem 0 0' }}>
            {t('dashboard.productionOrders.note')}
          </p>
        </Card>

        <Card className="b-5 glass" tilt>
          <span className={`badge ${s && s.openAndon > 0 ? 'danger' : 'success'}`}>
            <FontAwesomeIcon icon="tower-observation" /> {t('dashboard.andonOpen')}
          </span>
          <p className="kpi-num">
            {s ? <Counter value={s.openAndon} suffix={t('common.units.count')} /> : <Skeleton h={36} />}
          </p>
          <p className="muted" style={{ margin: '0.4rem 0 0', fontSize: 'var(--fs-sm)' }}>
            {s
              ? t('dashboard.pmDue7d', { n: s.dueMaintenance7d })
              : <Skeleton h={14} w={120} />}
          </p>
        </Card>

        <Card className="b-6 glass" tilt>
          <div className="row" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
            <span className="badge info">
              <FontAwesomeIcon icon="users" /> {t('dashboard.onDuty')}
            </span>
            {s && s.inventoryAccuracy !== null && (
              <span className={`badge ${s.inventoryAccuracy >= 99.5 ? 'success' : 'warn'}`}>
                <FontAwesomeIcon icon="warehouse" /> {t('dashboard.inventoryAccuracy', { pct: s.inventoryAccuracy.toFixed(1) })}
              </span>
            )}
          </div>
          <p className="kpi-num">
            {s ? <Counter value={s.onDutyWorkers} suffix={t('common.units.people')} /> : <Skeleton h={36} />}
          </p>
          {s && s.inventoryAccuracy === null && s.inventoryCountSamples === 0 && (
            <p className="muted" style={{ margin: '0.4rem 0 0', fontSize: 'var(--fs-xs)' }}>
              {t('dashboard.inventoryEmpty')}
            </p>
          )}
        </Card>
      </section>

      <section className="stack reveal" style={{ marginBlockStart: '2.5rem' }} aria-labelledby="eq-h">
        <div className="row space-between">
          <h2 id="eq-h" style={{ margin: 0 }}>{t('dashboard.eqStatusTitle')}</h2>
          <span className="muted">{t('dashboard.swipeHint')}</span>
        </div>
        {err && <p role="alert" className="badge danger">{err}</p>}
        <div className="h-scroll" role="list">
          {(eq ?? Array.from({ length: 4 })).map((row: any, i) => (
            <Card key={row?.equipment_id ?? i} role="listitem" className="glass" tilt spotlight>
              {row ? (
                <>
                  <span className="badge info">{row.equipment_id}</span>
                  <h3 style={{ marginBlock: '0.3rem 0.2rem' }}>{row.equipment_name}</h3>
                  <p className="mono kpi-num" style={{ margin: 0 }}>
                    <Counter value={row.oee} decimals={1} suffix="%" />
                  </p>
                  <div className="row" style={{ marginTop: '0.6rem', fontSize: 'var(--fs-sm)' }}>
                    <span>{t('dashboard.eqMetrics.operation')} {row.operation_rate.toFixed(1)}%</span>
                    <span>{t('dashboard.eqMetrics.performance')} {row.performance_rate.toFixed(1)}%</span>
                    <span>{t('dashboard.eqMetrics.quality')} {row.quality_rate.toFixed(1)}%</span>
                  </div>
                </>
              ) : (
                <SkeletonRows rows={4} />
              )}
            </Card>
          ))}
        </div>
      </section>

      <SocialProof />
    </main>
  )
}
